/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery } from '@apollo/client';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Badge, Box, Button, Center, Container, Flex, Stack, TableContainer, Text, useToast } from '@chakra-ui/react';
import { faFileAlt, faFileInvoice, faPercent, faPrint } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactPaginate from 'react-paginate';
import { useHistory, useLocation } from 'react-router-dom';
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';
import BreadcrumbComponent from '../../components/BreadcrumbComponent/BreadcrumbComponent';
import CompanyName from '../../components/CompanyName';
import LabelDate from '../../components/LabelDate';
import PrintLayout from '../../components/Print/PrintLayout';
import c from '../../constant';
import useStore from '../../store';
import { getReportTitle } from '../MarketDevelopment/Utils';
import Filter from './components/filter';

const Q_STATEMENTS = gql`
	query get(
		$pagerInput: PagerInput!
		$report: EarlyWarningTypeReport!
		$companyId: String
		$companyType: InsuranceInstitutionsType
		$group: EarlyWarningGroupTypeStatement
		$type: EarlyWarningTypeStatement
		$year: Float
		$isEditing: Boolean
	) {
		getEarlyWarningStatements(
			pagerInput: $pagerInput
			report: $report
			companyId: $companyId
			companyType: $companyType
			group: $group
			type: $type
			year: $year
			isEditing: $isEditing
		) {
			list {
				id
				company {
					id
					name
					nameKhmer
					type
					logo
				}
				year
				type
				createdAt
				isEditing
				editingUpdatedAt
				editingUpdatedBy
				editingDueDate
				editingDescription
				notification {
					read
				}
			}
			totalRows
			totalPages
		}
	}
`;

const group = {
	quarterly_report_on_investment: 'QuarterlyReportOnInvestment',
	quarterly_financial_statements: 'QuarterlyFinancialStatement',
	annually_unaudited_financial_statements: 'AnnuallyUnauditedFinancialStatements',
	annually_audited_financial_statements: 'AnnuallyAuditedFinancialStatements',
	annually_report_on_investment: 'AnnuallyReportOnInvestment',
};

function EarlyWarningList() {
	const toast = useToast();
	const location = useLocation();
	const urlParams = new URLSearchParams(location.search);
	const page = urlParams.get('page') || 1;
	const companyId = urlParams.get('companyId') || null;
	const companyType = urlParams.get('companyType') || null;
	const type = urlParams.get('type') || null;
	const year = urlParams.get('year') || null;
	const report = urlParams.get('report') || null;
	const editing = urlParams.has('editing', true);
	const period = urlParams.get('period') || null;
	const reportId = urlParams.get('reportId') || null;
	const { push } = useHistory();
	const { t } = useTranslation();
	const [fetchList, { loading: loadingGet, data: dataGet, error: errorGet }] = useLazyQuery(Q_STATEMENTS);
	const readReport = useStore((state) => state.readReport);
	const { currentUser: user } = useStore((state) => state?.currentUser);
	const [showReportButton, setShowReportButton] = useState(false);

	useEffect(() => {
		load();
	}, [page, companyId, companyType, type, year, editing, period, reportId]);

	useEffect(() => {
		if (errorGet) {
			if (errorGet?.graphQLErrors?.length > 0) {
				errorGet?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(errorGet?.message ? errorGet?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [errorGet]);

	function load() {
		const limit = 10;
		const offset = parseInt(page ? (page - 1) * limit : 0) || 0;
		const variables = {
			pagerInput: { limit, offset },
			report,
			companyId,
			companyType,
			group: group[reportId],
			type: type,
			year: parseInt(year),
			isEditing: editing ? true : null,
		};
		fetchList({ variables });
	}

	function handlePageChanged({ selected }) {
		let url = `?page=${selected + 1}&report=${report}`;

		if (companyId !== null && companyId !== undefined) {
			url += `&companyId=${companyId}`;
		}

		if (companyType !== null && companyType !== undefined) {
			url += `&companyType=${companyType}`;
		}

		if (type !== null && type !== undefined) {
			url += `&type=${type}`;
		}

		if (year !== null && year !== undefined) {
			url += `&year=${year}`;
		}

		if (editing !== null && editing !== undefined) {
			url += `&editing=${editing}`;
		}

		push(`/early-warning-system${url}`);
	}

	useEffect(() => {
		if (user) {
			const roles = user?.role.split(',') || [];
			const show = (roles?.includes('ews') && user?.ews?.includes('ewsCanViewSummarizedReport')) || roles?.includes('admin');
			setShowReportButton(show);
		}
	}, [user]);

	return (
		<PrintLayout>
			<>
				<Container maxW="container.xl" mt="16px" mb="16px">
					<BreadcrumbComponent
						list={[
							{
								name: 'Insurance Institute Report',
								path: '/company-submission-report',
							},
							{
								name: period,
								path: '/company-submission-report?period=' + period,
							},
							{
								name: getReportTitle(reportId),
								path: '#',
							},
						]}
					/>
					<Center mt="5">
						<Box
							w="100%"
							bg="white"
							borderRadius={c.borderRadius}
							border="1px solid #dbdbdb"
							boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
							pb="32px"
						>
							<Flex
								className="hidden-print responsive-header-for-table"
								bg="#FAFAFA"
								p="16px"
								pt="8px"
								pb="8px"
								mb="16px"
								alignItems="center"
								justifyContent="space-between"
								borderBottom="1px solid #dbdbdb"
							>
								<Text flex={{ base: 0, md: 1 }} fontSize="lg" fontWeight="bold" noOfLines={1} display={{ base: 'none', md: 'block' }}>
									{t('Company Report List')}
								</Text>
								<Stack
									flex={{ base: 1, md: 3 }}
									direction={{ base: 'column', md: 'row' }}
									justifyContent="flex-end"
									alignItems="center"
									className="hidden-print"
								>
									{showReportButton && (
										<Button
											className="hidden-print"
											isLoading={false}
											isDisabled={false}
											leftIcon={<FontAwesomeIcon icon={faFileInvoice} style={{ fontSize: 16 }} />}
											size="sm"
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											colorScheme="gray"
											variant="solid"
											mt={[2, 2, 0]}
											onMouseDown={() => {
												if (period === 'quarterly' && report === 'ir') {
													push(
														'/early-warning-system/quarterly-summarized-ir-report' +
															'?period=' +
															period +
															'&reportId=' +
															reportId +
															'&report=' +
															report,
													);
												} else if (period === 'quarterly' && report === 'ews') {
													push(
														'/early-warning-system/quarterly-summarized-fs-report' +
															'?period=' +
															period +
															'&reportId=' +
															reportId +
															'&report=' +
															report,
													);
												} else if (period === 'annually' && report === 'ir') {
													if (reportId === 'annually_report_on_investment') {
														push(
															'/early-warning-system/annually-summarized-ir-report' +
																'?period=' +
																period +
																'&reportId=' +
																reportId +
																'&report=' +
																report,
														);
													}
												} else if (period === 'annually' && report === 'ews') {
													if (
														reportId === 'annually_audited_financial_statements' ||
														reportId === 'annually_unaudited_financial_statements'
													) {
														push(
															'/early-warning-system/annually-summarized-fs-report' +
																'?period=' +
																period +
																'&reportId=' +
																reportId +
																'&report=' +
																report,
														);
													}
												}
											}}
										>
											{t('Summarized Report')}
										</Button>
									)}
									<Button
										mr="2"
										onClick={() => window?.print()}
										leftIcon={<FontAwesomeIcon icon={faPrint} style={{ fontSize: 16 }} />}
										colorScheme="gray"
										variant="solid"
										size="sm"
										borderRadius={c.borderRadius}
										border="1px solid #bdc3c7"
									>
										{t('Print')}
									</Button>
									<Filter
										period={period}
										reportId={reportId}
										companyType={companyType}
										companyId={companyId}
										type={type}
										report={report}
										year={year}
										editing={editing}
										onCallback={(url) => push(`/early-warning-system${url}`)}
									/>
								</Stack>
							</Flex>
							<Box p="16px" pt="0">
								{loadingGet && (
									<Flex justifyContent="center" alignItems="center">
										<Text>{t('Loading')}</Text>
									</Flex>
								)}
								{!loadingGet && dataGet?.getEarlyWarningStatements?.list?.length <= 0 && (
									<Flex direction="column" alignItems="center" justifyContent="center" pt={16} pl={4} pr={4} pb={16}>
										<FontAwesomeIcon icon={faFileAlt} style={{ fontSize: 56, color: '#A0AEC0' }} />
										<Text fontSize="lg" fontWeight="bold" color="gray.600" mt={6}>
											{t('No Data')}
										</Text>
									</Flex>
								)}
								{!loadingGet && dataGet?.getEarlyWarningStatements?.list?.length > 0 && (
									<>
										<TableContainer>
											<Table variant="simple" className="table-company-proposal-list responsive-table-container">
												<Thead>
													<Tr className="td-as-th">
														<Th align="left">{t('COMPANY')}</Th>
														{period?.toLowerCase() === 'quarterly' && <Th align="left">{t('Type')}</Th>}
														<Th align="left">{t('Year')}</Th>
														<Th align="left">{t('Created At')}</Th>
														<Th align="right">{t('Status')}</Th>
													</Tr>
												</Thead>
												<Tbody>
													{dataGet?.getEarlyWarningStatements?.list?.map((e, i) => {
														return (
															<Tr
																key={`report-item-${i}`}
																className={e?.notification?.read === false ? 'new' : 'read'}
																onMouseDown={() => {
																	if (!e?.notification?.read) {
																		readReport(reportId);
																	}
																	push(
																		'/early-warning-system/detail?id=' +
																			e?.id +
																			'&period=' +
																			period +
																			'&reportId=' +
																			reportId +
																			'&report=' +
																			report,
																	);
																}}
															>
																<Td align="left">
																	<CompanyName company={e?.company} />
																</Td>
																{period?.toLowerCase() === 'quarterly' && (
																	<Td align="left">
																		<Badge colorScheme="gray">{t(e?.type)}</Badge>
																	</Td>
																)}
																<Td align="left">
																	<LabelDate input={parseInt(e?.year)} inputFormat="Y" outputFormat="YYYY" />
																</Td>
																<Td align="left">
																	<LabelDate input={e?.createdAt} outputFormat={c.dateTimeFormat} />
																</Td>
																<Td align="right">
																	{e?.isEditing ? (
																		<Badge colorScheme="orange" variant="solid">
																			{t('Editing')}
																		</Badge>
																	) : (
																		<>-</>
																	)}
																</Td>
															</Tr>
														);
													})}
												</Tbody>
												<tfoot className="responsive-footer hidden-print">
													<Tr className="td-as-th">
														<Th align="left">{t('Company')}</Th>
														{period?.toLowerCase() === 'quarterly' && <Th align="left">{t('Type')}</Th>}
														<Th align="left">{t('Year')}</Th>
														<Th align="left">{t('Created At')}</Th>
														<Th align="right">{t('Status')}</Th>
													</Tr>
												</tfoot>
											</Table>
										</TableContainer>
										<Flex className="hidden-print" alignItems="flex-end" direction="column" mt={2} pl={6} pr={6}>
											<Text fontSize="sm" fontWeight="semibold" color="gray.600">
												{`${t('Total Rows')}: ${dataGet?.getEarlyWarningStatements?.totalRows}`}
											</Text>
										</Flex>
										{dataGet?.getEarlyWarningStatements?.totalPages > 1 && (
											<Flex className="hidden-print" justifyContent="flex-end" mt={8} pl={0} pr={0}>
												<ReactPaginate
													previousLabel={<ChevronLeftIcon fontSize="20px" />}
													nextLabel={<ChevronRightIcon fontSize="20px" />}
													breakLabel={'...'}
													breakClassName={'break-me'}
													disableInitialCallback={true}
													pageCount={dataGet?.getEarlyWarningStatements?.totalPages}
													marginPagesDisplayed={2}
													pageRangeDisplayed={3}
													onPageChange={handlePageChanged}
													containerClassName={'pagination'}
													activeClassName={'active'}
													initialPage={(page - 1) | 0}
												/>
											</Flex>
										)}
									</>
								)}
							</Box>
						</Box>
					</Center>
				</Container>
			</>
		</PrintLayout>
	);
}

export default EarlyWarningList;
